import { Box, Skeleton, Typography, ToggleButtonGroup, ToggleButton } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, PieChart, Pie, Cell, Legend } from 'recharts';
import { useDashboard } from '../DashboardContext';
import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import { useAppSocket } from '../../../providers/AppSocketContext';
import { DashboardInfoTip } from './DashboardInfoTip';
import DashboardWidget, { DashboardSizedChart } from './DashboardWidget';
import { useChartSurface } from '../dashboardChrome';

const COLORS = ['#2e7d32', '#0288d1', '#ed6c02', '#d32f2f', '#7b1fa2', '#00695c', '#c2185b', '#f57c00'];

const SalesChartBody = ({ chartView }: { chartView: 'trend' | 'modes' }) => {
  const { salesTrend, paymentModes, sectionLoading, currency } = useDashboard();
  const { t } = useTranslation();
  const { appSettings } = useAppSocket();
  const themeColor = appSettings?.UI_THEME_PRIMARY?.value || '#8b1010';
  const chrome = useChartSurface();

  const formatCurrency = (value: number) => {
    if (value >= 1000000) return `${(value / 1000000).toFixed(1)}M`;
    if (value >= 1000) return `${(value / 1000).toFixed(0)}K`;
    return value.toFixed(0);
  };

  const formatTooltipValue = (value: number) => {
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 0 }).format(value) + ` ${currency}`;
  };

  const chartLoading = chartView === 'trend' ? sectionLoading.sales : sectionLoading.payments;

  if (chartLoading) {
    return <Skeleton variant="rectangular" width="100%" height="100%" sx={{ borderRadius: 2 }} />;
  }

  if (chartView === 'trend') {
    if (salesTrend.length === 0) {
      return (
        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, color: 'text.secondary' }}>
          <Typography>{t('common:dashboard.noData')}</Typography>
        </Box>
      );
    }
    return (
      <DashboardSizedChart height={300}>
        <AreaChart data={salesTrend} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
          <defs>
            <linearGradient id="salesGradient" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor={themeColor} stopOpacity={0.3} />
              <stop offset="95%" stopColor={themeColor} stopOpacity={0} />
            </linearGradient>
          </defs>
          <CartesianGrid strokeDasharray="3 3" stroke={chrome.grid} />
          <XAxis
            dataKey="date"
            tickFormatter={(v) => {
              const d = new Date(v);
              return `${d.getDate()}/${d.getMonth() + 1}`;
            }}
            fontSize={12}
            stroke={chrome.axis}
          />
          <YAxis tickFormatter={formatCurrency} fontSize={12} stroke={chrome.axis} />
          <Tooltip
            formatter={(value: number) => [formatTooltipValue(value), t('common:dashboard.sales.amount')]}
            labelFormatter={(label) => new Date(label).toLocaleDateString()}
            contentStyle={chrome.tooltip}
          />
          <Area type="monotone" dataKey="amount" stroke={themeColor} strokeWidth={2} fill="url(#salesGradient)" />
        </AreaChart>
      </DashboardSizedChart>
    );
  }

  if (paymentModes.length === 0 || !paymentModes.some((m) => m.amount > 0)) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', flex: 1, minHeight: 200, color: 'text.secondary' }}>
        <Typography>{t('common:dashboard.noData')}</Typography>
      </Box>
    );
  }

  return (
    <DashboardSizedChart height={300}>
      <PieChart>
        <Pie
          data={paymentModes.filter((m) => m.amount > 0)}
          cx="50%"
          cy="50%"
          innerRadius={70}
          outerRadius={110}
          paddingAngle={3}
          dataKey="amount"
          nameKey="mode"
          label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`}
        >
          {paymentModes.filter((m) => m.amount > 0).map((_, idx) => (
            <Cell key={idx} fill={COLORS[idx % COLORS.length]} />
          ))}
        </Pie>
        <Tooltip formatter={(value: number) => formatTooltipValue(value)} />
        <Legend />
      </PieChart>
    </DashboardSizedChart>
  );
};

const SalesChart = () => {
  const { sectionLoading } = useDashboard();
  const { t } = useTranslation();
  const [chartView, setChartView] = useState<'trend' | 'modes'>('trend');
  const chartLoading = sectionLoading.sales || sectionLoading.payments;

  return (
    <DashboardWidget
      title={t('common:dashboard.sales.title')}
      helpKey="sales.title"
      chartHeight={300}
      headerActions={
        !chartLoading ? (
          <ToggleButtonGroup
            size="small"
            value={chartView}
            exclusive
            onChange={(_, v) => v && setChartView(v)}
          >
            <ToggleButton value="trend" sx={{ textTransform: 'none', px: 2, gap: 0.25 }}>
              {t('common:dashboard.sales.trend')}
              <DashboardInfoTip helpKey="sales.trend" />
            </ToggleButton>
            <ToggleButton value="modes" sx={{ textTransform: 'none', px: 2, gap: 0.25 }}>
              {t('common:dashboard.sales.byMode')}
              <DashboardInfoTip helpKey="sales.byMode" />
            </ToggleButton>
          </ToggleButtonGroup>
        ) : undefined
      }
    >
      <SalesChartBody chartView={chartView} />
    </DashboardWidget>
  );
};

export default SalesChart;